import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { ArrowLeft } from 'lucide-react';

interface ResetPasswordFormProps {
  onSuccess?: () => void;
  onBack?: () => void;
}

export function ResetPasswordForm({
  onSuccess,
  onBack,
}: ResetPasswordFormProps) {
  const { resetPassword, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const validateEmail = () => {
    if (!email) {
      setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail()) return;

    const result = await resetPassword(email);

    if (result.success) {
      setSent(true);
      toast({
        title: 'Reset link sent',
        description: 'Check your email for the password reset link.',
        variant: 'success',
      });
    } else {
      toast({
        title: 'Error',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  if (sent) {
    return (
      <div className="space-y-4 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sapling-primary-100/10">
          <svg
            className="h-6 w-6 text-sapling-primary-100"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <div>
          <h3 className="font-semibold">Check your email</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            We've sent a password reset link to{' '}
            <span className="font-medium text-foreground">{email}</span>
          </p>
        </div>
        <Button variant="outline" className="w-full" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to login
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Enter your email address and we'll send you a link to reset your
        password.
      </p>

      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="reset-email">Email</Label>
        <Input
          id="reset-email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!error}
          disabled={loading}
          autoComplete="email"
        />
        {error && <p className="text-sm text-sapling-error-100">{error}</p>}
      </div>

      {/* Submit button */}
      <Button type="submit" className="w-full" loading={loading}>
        Send Reset Link
      </Button>

      {/* Back to login */}
      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={onBack}
        disabled={loading}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to login
      </Button>
    </form>
  );
}
